import React, { useMemo } from "react";

function emptyState() {
  return {
    id: "",
    name: "",
    x: 0,
    y: 0,
    z: 0,
    diameter: 5,
    depth: 18,
    kind: "",
  };
}

export default function MountingElementInspector({
  model,
  selectedElement,
  onChangeModel,
}) {
  const safeModel = model || {
    origin: { x: 0, y: 0, z: 0 },
    attachPoints: [],
    holes: [],
    cutouts: [],
  };

  const selectedItem = useMemo(() => {
    if (!selectedElement) return null;

    if (selectedElement.type === "origin") {
      return {
        type: "origin",
        item: {
          id: "origin",
          name: "Origin",
          ...(safeModel.origin || {}),
        },
      };
    }

    const list =
      selectedElement.type === "attach"
        ? safeModel.attachPoints || []
        : selectedElement.type === "hole"
        ? safeModel.holes || []
        : selectedElement.type === "cutout"
        ? safeModel.cutouts || []
        : [];

    const found = list.find((x) => x.id === selectedElement.id);
    if (!found) return null;

    return {
      type: selectedElement.type,
      item: {
        ...emptyState(),
        ...found,
      },
    };
  }, [selectedElement, safeModel]);

  function patchSelected(field, value) {
    if (!selectedItem) return;

    if (selectedItem.type === "origin") {
      onChangeModel?.({
        ...safeModel,
        origin: {
          ...(safeModel.origin || {}),
          [field]: value,
        },
      });
      return;
    }

    const targetKey =
      selectedItem.type === "attach"
        ? "attachPoints"
        : selectedItem.type === "hole"
        ? "holes"
        : "cutouts";

    onChangeModel?.({
      ...safeModel,
      [targetKey]: (safeModel[targetKey] || []).map((item) =>
        item.id === selectedItem.item.id ? { ...item, [field]: value } : item
      ),
    });
  }

  return (
    <div className="p-3 border-l h-full overflow-auto space-y-4">
      <h3 className="font-semibold text-lg">Mounting Inspector</h3>

      {!selectedItem ? (
        <div className="text-sm text-gray-500 border rounded p-3">
          Select origin, attach point, hole, or cutout.
        </div>
      ) : (
        <>
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <input
              value={selectedItem.type}
              readOnly
              className="w-full px-3 py-2 border rounded bg-gray-50"
            />
          </div>

          {selectedItem.type !== "origin" ? (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  value={selectedItem.item.name || ""}
                  onChange={(e) => patchSelected("name", e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Kind</label>
                <input
                  value={selectedItem.item.kind || ""}
                  onChange={(e) => patchSelected("kind", e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
            </>
          ) : null}

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-sm font-medium mb-1">X</label>
              <input
                type="number"
                value={selectedItem.item.x ?? 0}
                onChange={(e) => patchSelected("x", Number(e.target.value) || 0)}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Y</label>
              <input
                type="number"
                value={selectedItem.item.y ?? 0}
                onChange={(e) => patchSelected("y", Number(e.target.value) || 0)}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Z</label>
              <input
                type="number"
                value={selectedItem.item.z ?? 0}
                onChange={(e) => patchSelected("z", Number(e.target.value) || 0)}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
          </div>

          {selectedItem.type === "hole" || selectedItem.type === "cutout" ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Diameter</label>
                  <input
                    type="number"
                    value={selectedItem.item.diameter ?? 5}
                    onChange={(e) =>
                      patchSelected("diameter", Number(e.target.value) || 0)
                    }
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Depth</label>
                  <input
                    type="number"
                    value={selectedItem.item.depth ?? 18}
                    onChange={(e) =>
                      patchSelected("depth", Number(e.target.value) || 0)
                    }
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}